import { NOMINATIM_URL } from './constants.js';

/**
 * Initialise search panel, HUD, GPS button, loading overlay and error toast.
 *
 * @param {Function} onLoadArea  Called with (lat, lng)
 * @returns {{ updateHUD, showLoading, hideLoading, showError }}
 */
export function initUI(onLoadArea) {
  const searchInput = document.getElementById('search-input');
  const searchBtn   = document.getElementById('search-btn');
  const gpsBtn      = document.getElementById('gps-btn');
  const loadingEl   = document.getElementById('loading-overlay');
  const loadingText = document.getElementById('loading-text');
  const errorToast  = document.getElementById('error-toast');
  const hudSpeed    = document.getElementById('hud-speed');
  const hudOdo      = document.getElementById('hud-odo');
  const areaName    = document.getElementById('area-name');

  // ── Search by text ────────────────────────────────────────────────────────
  async function doSearch() {
    const query = searchInput.value.trim();
    if (!query) return;
    showLoading('Searching…');
    try {
      const url = `${NOMINATIM_URL}?q=${encodeURIComponent(query)}&format=json&limit=1`;
      const res  = await fetch(url, {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'OSM3D-Simulator/1.0 (https://github.com/gcerretani/OSM3D)',
        },
      });
      const results = await res.json();
      if (!results.length) {
        showError('Location not found. Try a more specific search.');
        hideLoading();
        return;
      }
      const { lat, lon, display_name } = results[0];
      showLoading('Loading OSM data…');
      await onLoadArea(parseFloat(lat), parseFloat(lon));
      setAreaName(display_name.split(',').slice(0, 2).join(', '));
    } catch (err) {
      console.error(err);
      showError('Error: ' + err.message);
    } finally {
      hideLoading();
    }
  }

  searchBtn.addEventListener('click', doSearch);
  searchInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSearch(); });

  // ── GPS (current position) ─────────────────────────────────────────────────
  if (gpsBtn) {
    gpsBtn.addEventListener('click', () => {
      if (!navigator.geolocation) {
        showError('Geolocation is not supported by this browser.');
        return;
      }

      // Visual feedback while waiting
      gpsBtn.classList.add('locating');
      showLoading('Getting GPS position…');

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          try {
            showLoading('Loading OSM data…');
            await onLoadArea(latitude, longitude);
            const acc = accuracy < 100 ? ` (±${Math.round(accuracy)} m)` : '';
            setAreaName(`📍 ${latitude.toFixed(5)}, ${longitude.toFixed(5)}${acc}`);
          } catch (err) {
            showError('Error loading area: ' + err.message);
          } finally {
            gpsBtn.classList.remove('locating');
            hideLoading();
          }
        },
        (err) => {
          gpsBtn.classList.remove('locating');
          hideLoading();
          const msgs = {
            1: 'GPS denied. Allow location access and try again.',
            2: 'GPS position unavailable.',
            3: 'GPS request timed out.',
          };
          showError(msgs[err.code] || 'GPS error: ' + err.message);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
      );
    });
  }

  // ── Public API ────────────────────────────────────────────────────────────
  function showLoading(text = 'Loading…') {
    loadingText.textContent = text;
    loadingEl.classList.add('visible');
  }

  function hideLoading() {
    loadingEl.classList.remove('visible');
  }

  let errorTimer = null;
  function showError(msg) {
    errorToast.textContent = msg;
    errorToast.classList.add('visible');
    clearTimeout(errorTimer);
    errorTimer = setTimeout(() => errorToast.classList.remove('visible'), 5000);
  }

  function setAreaName(text) {
    areaName.textContent = text;
    areaName.style.display = 'block';
  }

  function updateHUD(carState) {
    hudSpeed.textContent = Math.abs(Math.round(carState.speed * 3.6));
    if (hudOdo) {
      const m  = carState.odometer;
      const km = m / 1000;
      hudOdo.textContent = m < 1000  ? Math.round(m)      + ' m'
                         : km < 10   ? km.toFixed(2)       + ' km'
                         : km < 100  ? km.toFixed(1)       + ' km'
                         :             Math.round(km)      + ' km';
    }
  }

  /** Non-blocking status badge — used for seamless auto-reload notifications. */
  function setStatus(text) {
    areaName.textContent = text;
    areaName.style.display = 'block';
  }

  return { updateHUD, showLoading, hideLoading, showError, setAreaName, setStatus };
}
